import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";

/**
 * Interactive 3D car damage viewer (Clearline redesign, mock 3a).
 * Requires: npm install three
 *
 * Props:
 *   damage        { [partKey]: "severe" | "moderate" | "minor" }
 *   selectedPart  partKey | null   (controlled selection)
 *   onSelectPart  (partKey) => void
 *   viewPreset    "threeq" | "front" | "rear" | "left" | "right" | "top"
 *   autoRotate    boolean (default true; stops on first user drag)
 *   model         key into MODEL_PROFILES (default "3dcar")
 *
 * Models live in /public and tag their assemblies with glTF `extras.partId`.
 * Each entry in MODEL_PROFILES translates one model's ids into this app's part
 * keys, so the contract used by carPartMapping.js and the Damage report tab
 * never changes when the mesh is swapped. If the GLB can't be fetched a
 * schematic low-poly stand-in is built instead.
 *
 * Part keys: front_bumper, rear_bumper, bonnet, boot, roof, grille,
 *   fender_l/r, door_fl/fr/rl/rr, quarter_l/r, lamp_l/r, tail_l/r, fog_l/r,
 *   mirror_l/r, skirt_l/r, windshield, rear_windshield, window_fl/fr/rl/rr,
 *   quarter_glass_l/r, pillar_a_l/r, pillar_b_l/r, pillar_c_l/r,
 *   wheel_fl/fr/rl/rr, fuel_cap, shell
 */
// Kept in sync by hand with SEVERITY_META's own colours in carPartMapping.js
// (the legend swatches and damaged-parts-list dots use that copy) — both
// need to agree or the 3D panel and its own legend show two different reds
// for "severe".
const SEV_COLORS = { severe: 0xeb8a8a, moderate: 0xf4b685, minor: 0xf6d68c };
// A plain scalar darken (multiplyScalar) keeps the same hue AND saturation,
// just dimmer — on these pastel severity colours that read as too close to
// the unselected panel to tell apart at a glance. Blending toward a neutral
// grey instead darkens *and* desaturates, which is what actually reads as a
// distinct "selected" state rather than a slightly dimmer one. Still derived
// from SEV_COLORS rather than a hand-picked sibling palette, so the two can't
// drift out of sync if a severity colour ever changes.
const SELECT_TINT_GREY = 0x45484f;
const SELECT_TINT_BLEND = 0.6;
const SEV_COLORS_SELECTED = Object.fromEntries(
  Object.entries(SEV_COLORS).map(([key, hex]) => [
    key,
    new THREE.Color(hex)
      .lerp(new THREE.Color(SELECT_TINT_GREY), SELECT_TINT_BLEND)
      .getHex(),
  ]),
);
const PAINT = 0xdfe3ea,
  TRIM = 0x313743,
  GLASS = 0x9fb7cc;

// Steady "selected" glow for a panel WITH a severity: a small accent on top
// of SEV_COLORS_SELECTED's darker base, so selection reads as "darker and
// subtly lit" rather than darker alone — kept well under the old
// SELECT_EMISSIVE ceiling (0.2, where a *full-brightness* severe panel starts
// reading pink) since it's now adding to an already-darker base with more
// headroom before the same wash would show.
const SELECT_TINT_ACCENT = 0.1;
// Steady "selected" glow for a panel with NO severity (nothing in
// SEV_COLORS_SELECTED to darken toward) — brightening is the only available
// way to show it's selected there.
const SELECT_EMISSIVE = 0.2;
const HOVER_EMISSIVE = 0.09;

// A click flashes the panel well above its steady level, then decays back. The
// steady value has to stay under the wash threshold described above, but a brief
// peak does not — it is gone before the eye reads the tint as shifted. This is
// what makes a click legible in the 3D view itself, rather than only in the card
// that updates beside it.
const SELECT_FLASH_PEAK = 0.44;
const SELECT_FLASH_MS = 420;

// Once, shortly after the model appears, the damaged panels breathe together.
// Hover already lifts a panel and the cursor already changes, but neither is
// discoverable without moving the mouse onto the car first — this shows the
// panels are live before the user tries anything. Cancelled the moment they
// interact, so it never fights a real hover or selection.
const HINT_PULSE_PEAK = 0.13;
const HINT_PULSE_MS = 1700;

const LABELS = {
  front_bumper: "Front bumper",
  rear_bumper: "Rear bumper",
  bonnet: "Bonnet",
  roof: "Roof",
  boot: "Boot lid",
  grille: "Front grille",
  fender_l: "Left fender",
  fender_r: "Right fender",
  door_fl: "Left front door",
  door_fr: "Right front door",
  door_rl: "Left rear door",
  door_rr: "Right rear door",
  quarter_l: "Left quarter panel",
  quarter_r: "Right quarter panel",
  lamp_l: "Left head lamp",
  lamp_r: "Right head lamp",
  tail_l: "Left tail lamp",
  tail_r: "Right tail lamp",
  fog_l: "Left fog lamp",
  fog_r: "Right fog lamp",
  mirror_l: "Left mirror",
  mirror_r: "Right mirror",
  skirt_l: "Left running board",
  skirt_r: "Right running board",
  windshield: "Front windshield",
  rear_windshield: "Rear windshield",
  window_fl: "Left front window",
  window_fr: "Right front window",
  window_rl: "Left rear window",
  window_rr: "Right rear window",
  quarter_glass_l: "Left quarter glass",
  quarter_glass_r: "Right quarter glass",
  pillar_a_l: "Left A-pillar",
  pillar_a_r: "Right A-pillar",
  pillar_b_l: "Left B-pillar",
  pillar_b_r: "Right B-pillar",
  pillar_c_l: "Left C-pillar",
  pillar_c_r: "Right C-pillar",
  wheel_fl: "Left front rim",
  wheel_fr: "Right front rim",
  wheel_rl: "Left rear rim",
  wheel_rr: "Right rear rim",
  fuel_cap: "Fuel cap",
  shell: "Body shell",
};

const publicUrl = (file) => `${process.env.PUBLIC_URL || ""}/${file}`;

/**
 * A cold, saturated emissive glow — decorative rather than a lamp.
 *
 * The e-tron bakes a blue underglow into its chassis (emissive 0.05/0.212/0.745
 * at strength 0.85), which reads as a blue tint through the wheel arches and
 * under the bumpers, and competes with the severity colours this viewer exists
 * to communicate. Warm and white lamp emissives fail this test and are kept.
 */
const isDecorativeGlow = (material) => {
  const e = material?.emissive;
  return !!e && e.b > 0.3 && e.b > e.r * 2 && e.b > e.g * 2;
};

/**
 * Windshields and windows are tracked parts too (window_fl, windshield, …),
 * so they go through the same per-mesh material pass as the body panels —
 * the matte-finish pass below needs to leave these alone, or glass comes out
 * looking frosted instead of clear. `transparent`/KHR_materials_transmission
 * (three.js's `transmission`) are the two ways a glTF exporter marks real
 * glass; either one is enough to exclude it.
 */
const isGlassLike = (material) =>
  material?.transparent === true || (material?.transmission ?? 0) > 0;

/**
 * Per-model profile.
 *
 *   partIds     glTF extras.partId → app part key. Ids left out are still
 *               rendered, just not selectable or tinted (interior, chassis,
 *               brake hardware …). Several ids may share one key — a mirror
 *               housing and its glass both belong to `mirror_l`.
 *   leftAxis    which model-space axis the driver's left side faces. Models
 *               disagree on this, and it decides whether the Left/Right camera
 *               presets show the side they claim to.
 */
const MODEL_PROFILES = {
  // Photoreal coupe. Body panels are coarser than the sedan's: fenders,
  // quarter panels, roof, grille, fog lamps, pillars and running boards are
  // all baked into one `body` mesh, and the model has no right rear door or
  // right rear window. Damage on those panels can't be highlighted; the detail
  // card says "not on the 3D model" and the numbers still read normally.
  "3dcar": {
    label: "e-tron GT (unmarked)",
    file: "3dcar.glb",
    leftAxis: "+x",
    partIds: {
      body: "shell",
      hood: "bonnet",
      boot: "boot",
      frontbumper: "front_bumper",
      rearbumper: "rear_bumper",
      door_left: "door_fl",
      door_right: "door_fr",
      door_left_rear: "door_rl",
      headlight_lens_left: "lamp_l",
      headlight_lens_right: "lamp_r",
      taillight_lens_left: "tail_l",
      taillight_lens_right: "tail_r",
      mirror_left: "mirror_l",
      mirror_glass_left: "mirror_l",
      mirror_right: "mirror_r",
      mirror_glass_right: "mirror_r",
      glass_front: "windshield",
      glass_rear: "rear_windshield",
      glass_left: "window_fl",
      glass_right: "window_fr",
      glass_left_rear: "window_rl",
      glass_left_quarter: "quarter_glass_l",
      glass_right_quarter: "quarter_glass_r",
      wheel_front_left: "wheel_fl",
      tyre_front_left: "wheel_fl",
      wheel_front_right: "wheel_fr",
      tyre_front_right: "wheel_fr",
      wheel_rear_left: "wheel_rl",
      tyre_rear_left: "wheel_rl",
      wheel_rear_right: "wheel_rr",
      tyre_rear_right: "wheel_rr",
    },
  },

  // The same e-tron GT as "3dcar" but properly marked: 68 tagged assemblies
  // against the coupe's 55, named to match the assessment API's own vocabulary
  // (left_fender, left_apillar, grill, left_running_board …). Every id owns its
  // geometry — no two share a mesh — so tinting is panel-accurate. The cost is
  // weight: 40 MB, the heaviest of the three.
  etron: {
    label: "e-tron GT",
    file: "etron_gt_marked.glb",
    leftAxis: "+x",
    partIds: {
      front_bumper: "front_bumper",
      rear_bumper: "rear_bumper",
      hood: "bonnet",
      boot: "boot",
      outside_roof: "roof",
      grill: "grille",
      left_fender: "fender_l",
      right_fender: "fender_r",
      left_front_door: "door_fl",
      right_front_door: "door_fr",
      left_rear_door: "door_rl",
      right_rear_door: "door_rr",
      left_quarter_panel: "quarter_l",
      right_quarter_panel: "quarter_r",
      left_head_lamp: "lamp_l",
      right_head_lamp: "lamp_r",
      left_tail_lamp: "tail_l",
      right_tail_lamp: "tail_r",
      left_fog_lamp: "fog_l",
      right_fog_lamp: "fog_r",
      left_mirror: "mirror_l",
      right_mirror: "mirror_r",
      left_running_board: "skirt_l",
      right_running_board: "skirt_r",
      front_windshield: "windshield",
      rear_windshield: "rear_windshield",
      left_front_window: "window_fl",
      right_front_window: "window_fr",
      left_rear_window: "window_rl",
      right_rear_window: "window_rr",
      left_quarter_glass: "quarter_glass_l",
      right_quarter_glass: "quarter_glass_r",
      left_apillar: "pillar_a_l",
      right_apillar: "pillar_a_r",
      // Rim and tyre together, so the whole wheel is the click target.
      left_front_rim: "wheel_fl",
      left_front_tyre: "wheel_fl",
      right_front_rim: "wheel_fr",
      right_front_tyre: "wheel_fr",
      left_rear_rim: "wheel_rl",
      left_rear_tyre: "wheel_rl",
      right_rear_rim: "wheel_rr",
      right_rear_tyre: "wheel_rr",
    },
  },

  // Schematic sedan: less pretty than the coupe, but nearly every panel the
  // assessment API reports is a separate mesh, and it's ~15x lighter.
  // NB: this model was revised to label sides in the vehicle frame — its
  // driver's left is +X, mirrored from the version it replaced.
  sedan: {
    label: "Premium sedan",
    file: "generic_premium_sedan.glb",
    leftAxis: "+x",
    partIds: {
      front_bumper: "front_bumper",
      rear_bumper: "rear_bumper",
      hood: "bonnet",
      trunk: "boot",
      roof: "roof",
      front_grille: "grille",
      front_left_fender: "fender_l",
      front_right_fender: "fender_r",
      front_left_door: "door_fl",
      front_right_door: "door_fr",
      rear_left_door: "door_rl",
      rear_right_door: "door_rr",
      rear_left_fender: "quarter_l",
      rear_right_fender: "quarter_r",
      front_left_headlight: "lamp_l",
      front_right_headlight: "lamp_r",
      rear_left_tail_light: "tail_l",
      rear_right_tail_light: "tail_r",
      front_left_fog_lamp: "fog_l",
      front_right_fog_lamp: "fog_r",
      left_side_mirror: "mirror_l",
      right_side_mirror: "mirror_r",
      left_side_skirt: "skirt_l",
      right_side_skirt: "skirt_r",
      windshield: "windshield",
      rear_windshield: "rear_windshield",
      front_left_window: "window_fl",
      front_right_window: "window_fr",
      rear_left_window: "window_rl",
      rear_right_window: "window_rr",
      rear_left_quarter_glass: "quarter_glass_l",
      rear_right_quarter_glass: "quarter_glass_r",
      cap: "fuel_cap",
      left_a_pillar: "pillar_a_l",
      right_a_pillar: "pillar_a_r",
      left_b_pillar: "pillar_b_l",
      right_b_pillar: "pillar_b_r",
      left_c_pillar: "pillar_c_l",
      right_c_pillar: "pillar_c_r",
      front_left_wheel: "wheel_fl",
      front_right_wheel: "wheel_fr",
      rear_left_wheel: "wheel_rl",
      rear_right_wheel: "wheel_rr",
    },
  },
};

// The refreshed sedan separates 43 of its panels, so it can point at 38 of the
// 39 parts the assessment API reports (the coupe manages 24) and is ~15x
// lighter. Switch to "3dcar" for the photoreal look at that cost.
export const DEFAULT_MODEL = "sedan";

/**
 * Models offered in the UI picker. "3dcar" is deliberately absent: it is the
 * same vehicle as "etron" with coarser marking, kept in the registry only so it
 * can be put back in one line.
 */
export const MODEL_OPTIONS = ["sedan", "etron"].map((id) => ({
  id,
  label: MODEL_PROFILES[id].label,
}));

/** Which app part keys a given model can actually point at. */
export const locatableKeysFor = (model = DEFAULT_MODEL) =>
  new Set(Object.values(MODEL_PROFILES[model]?.partIds || {}));

// Both bundled models are authored nose-along-+Z, so a single yaw puts the
// front at +X where the camera presets expect it.
const MODEL_YAW = Math.PI / 2;
const TARGET_LENGTH = 4.6;

const BASE_VIEWS = {
  threeq: [4.4, 2.6, -4.6],
  front: [6.2, 1.7, 0],
  rear: [-6.2, 1.7, 0],
  left: [0, 1.9, 6.2],
  right: [0, 1.9, -6.2],
  top: [0.01, 7.4, 0],
};

/**
 * After the yaw above, a model whose left side faces +X ends up with that side
 * at -Z — mirrored relative to one whose left faces -X. Negating the camera's Z
 * puts every preset back on the flank it names, the default ¾ view included.
 */
const viewsFor = (profile) => {
  if (profile?.leftAxis !== "+x") return BASE_VIEWS;
  return Object.fromEntries(
    Object.entries(BASE_VIEWS).map(([name, [x, y, z]]) => [name, [x, y, -z]]),
  );
};

/**
 * One fetch/parse per URL for the whole app session — the scene is cloned per
 * mount. Geometries stay shared, so unmount disposes only what it created.
 */
const modelCache = new Map();

const loadVehicleModel = (url, onProgress) => {
  if (!modelCache.has(url)) {
    const promise = new Promise((resolve, reject) => {
      new GLTFLoader().load(url, resolve, onProgress, reject);
    })
      .then((gltf) => gltf.scene)
      .catch((error) => {
        modelCache.delete(url); // let a later mount retry
        throw error;
      });
    modelCache.set(url, promise);
  }
  return modelCache.get(url);
};

/** Rotate, scale and seat the model so the camera presets frame it. */
const orientModel = (root) => {
  root.rotation.y = MODEL_YAW;
  root.updateMatrixWorld(true);

  const size = new THREE.Box3()
    .setFromObject(root)
    .getSize(new THREE.Vector3());
  if (size.x > 0) root.scale.setScalar(TARGET_LENGTH / size.x);
  root.updateMatrixWorld(true);

  const box = new THREE.Box3().setFromObject(root);
  const center = box.getCenter(new THREE.Vector3());
  root.position.x -= center.x;
  root.position.z -= center.z;
  root.position.y -= box.min.y;
  root.updateMatrixWorld(true);
};

export default function CarDamage3D({
  damage = {},
  selectedPart = null,
  onSelectPart,
  viewPreset = "threeq",
  autoRotate = true,
  model = DEFAULT_MODEL,
}) {
  const hostRef = useRef(null);
  const apiRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | fallback
  const [progress, setProgress] = useState(0);

  // Latest props, readable from inside the long-lived setup effect.
  const cbRef = useRef(onSelectPart);
  cbRef.current = onSelectPart;
  const damageRef = useRef(damage);
  damageRef.current = damage;
  const selectedRef = useRef(selectedPart);
  selectedRef.current = selectedPart;
  const viewRef = useRef(viewPreset);
  viewRef.current = viewPreset;

  useEffect(() => {
    const host = hostRef.current;
    const profile = MODEL_PROFILES[model] || MODEL_PROFILES[DEFAULT_MODEL];
    const VIEWS = viewsFor(profile);
    let disposed = false;

    setStatus("loading");
    setProgress(0);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8f9fb);
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 60);
    camera.position.set(...VIEWS.threeq);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.domElement.style.cssText = "display:block;width:100%;height:100%";
    host.appendChild(renderer.domElement);

    // The photoreal model's clearcoat and transmissive glass need something to
    // reflect; without an environment they render flat and near-black. Kept
    // dim so it lifts the materials without shifting the severity tints.
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envRT = pmrem.fromScene(new RoomEnvironment(), 0.04);
    scene.environment = envRT.texture;
    scene.environmentIntensity = 0.45;
    pmrem.dispose();

    const tip = document.createElement("div");
    tip.style.cssText =
      "position:absolute;pointer-events:none;background:#131720;color:#fff;font:600 11px 'Schibsted Grotesk',sans-serif;padding:5px 9px;border-radius:6px;opacity:0;transition:opacity .12s;white-space:nowrap;z-index:3";
    host.appendChild(tip);

    scene.add(new THREE.HemisphereLight(0xffffff, 0xd8dce4, 1.15));
    const key = new THREE.DirectionalLight(0xffffff, 1.5);
    key.position.set(5, 8, -4);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.left = key.shadow.camera.bottom = -4;
    key.shadow.camera.right = key.shadow.camera.top = 4;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xdfe8ff, 0.5);
    fill.position.set(-5, 4, 5);
    scene.add(fill);
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(20, 20),
      new THREE.ShadowMaterial({ opacity: 0.14 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    const car = new THREE.Group();
    scene.add(car);

    // partKey → { meshes: [{ mesh, materials, base* }] }
    const parts = new Map();
    const partOfMesh = new Map();
    const picks = [];
    const ownedMaterials = [];
    const ownedGeometries = [];

    const recordMesh = (mesh, partKey) => {
      if (partOfMesh.has(mesh)) return;
      // Clone per mesh: the cached model shares materials across assemblies
      // (every panel uses one CarPaint), so tinting one damaged panel would
      // otherwise repaint half the car.
      mesh.material = Array.isArray(mesh.material)
        ? mesh.material.map((m) => m.clone())
        : mesh.material.clone();
      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];
      // Wheels are tracked parts too, and alloy rims are *supposed* to look
      // metallic — capping metalness there would flatten them to painted
      // plastic. Scoped to the body/trim/lamp parts the shininess complaint
      // is actually about.
      const isPaintLikePart = !partKey?.startsWith("wheel_");
      materials.forEach((m) => {
        ownedMaterials.push(m);
        // The GLB's car-paint shader is a glossy clearcoat finish — reads as
        // wet plastic under this scene's lighting rather than solid paint.
        // Flattened toward matte without touching .color, so hue/saturation
        // (and applyDamage's severity tint math) are unaffected — only how
        // sharp a highlight the surface bounces back. Math.max/min rather
        // than a flat assignment, so a part that's already matte (trim)
        // isn't pushed glossier than the artist set it. Glass is excluded
        // entirely — windows are tracked parts too, and the same pass would
        // otherwise frost them.
        if (isPaintLikePart && !isGlassLike(m)) {
          if ("roughness" in m) m.roughness = Math.max(m.roughness ?? 0, 0.6);
          if ("metalness" in m) m.metalness = Math.min(m.metalness ?? 0, 0.12);
          if ("clearcoat" in m) m.clearcoat = 0;
          if ("envMapIntensity" in m) {
            m.envMapIntensity = Math.min(m.envMapIntensity ?? 1, 0.5);
          }
        }
      });
      mesh.castShadow = true;

      if (!parts.has(partKey)) parts.set(partKey, { meshes: [] });
      parts.get(partKey).meshes.push({
        mesh,
        materials,
        baseColor: materials.map((m) => m.color.clone()),
        baseEmissive: materials.map((m) => m.emissive.clone()),
        baseIntensity: materials.map((m) => m.emissiveIntensity),
      });
      partOfMesh.set(mesh, partKey);
      picks.push(mesh);
    };

    /* ── Damage tint ─────────────────────────────────────────────────── */
    const applyDamage = (damageMap) => {
      parts.forEach((part, partKey) => {
        const severity = damageMap?.[partKey];
        part.meshes.forEach((entry) => {
          entry.materials.forEach((material, index) => {
            if (severity) {
              // Selected gets the darker sibling of its own severity colour
              // instead of the plain one — re-applied here (not just at
              // select time) so a damage-map refresh while a panel is
              // selected can't silently pop it back to the normal shade.
              material.color.setHex(
                partKey === selected
                  ? SEV_COLORS_SELECTED[severity]
                  : SEV_COLORS[severity],
              );
            } else {
              material.color.copy(entry.baseColor[index]);
            }
          });
        });
      });
    };

    const setPartEmissive = (partKey, amount) => {
      const part = parts.get(partKey);
      if (!part) return;
      part.meshes.forEach((entry) => {
        entry.materials.forEach((material, index) => {
          if (amount > 0) {
            material.emissive.setScalar(amount);
            material.emissiveIntensity = 1;
          } else {
            material.emissive.copy(entry.baseEmissive[index]);
            material.emissiveIntensity = entry.baseIntensity[index];
          }
        });
      });
    };

    // The steady (post-flash) glow level for a selected panel: none for a
    // panel with a severity (SEV_COLORS_SELECTED/applyDamage already makes it
    // read as selected via colour alone), SELECT_EMISSIVE otherwise.
    const steadyEmissiveFor = (partKey) =>
      damageRef.current?.[partKey] ? SELECT_TINT_ACCENT : SELECT_EMISSIVE;

    /* ── Selection ───────────────────────────────────────────────────── */
    let hovered = null; // partKey
    let selected = null; // partKey
    let downAt = null;
    let goal = null;

    // Animation state, advanced by the render loop rather than by timers so it
    // pauses with the loop when the canvas scrolls out of view.
    let flash = null; // { partKey, start } — decaying click highlight
    let hint = null; // { start } — one-off "panels are interactive" pulse
    let hintDone = false;

    // Any real interaction ends the hint; it must never compete with hover.
    const cancelHint = () => {
      if (!hint) return;
      const pulsed = hint.parts;
      hint = null;
      hintDone = true;
      pulsed.forEach((key) => {
        if (key === selected) setPartEmissive(key, steadyEmissiveFor(key));
        else if (key === hovered) setPartEmissive(key, HOVER_EMISSIVE);
        else setPartEmissive(key, 0);
      });
    };

    const applySelect = (partKey, { animate = false } = {}) => {
      const next = partKey && parts.has(partKey) ? partKey : null;
      if (selected && selected !== hovered) setPartEmissive(selected, 0);
      const changed = next !== selected;
      selected = next;
      // Re-tint every panel for the new selection: the one just selected (if
      // it has a severity) gets the darker shade, the one just deselected
      // settles back to its normal severity colour.
      applyDamage(damageRef.current);
      if (!next) {
        flash = null;
        return;
      }
      if (animate && changed) {
        cancelHint();
        flash = { partKey: next, start: performance.now() };
        setPartEmissive(next, SELECT_FLASH_PEAK);
      } else {
        flash = null;
        setPartEmissive(next, steadyEmissiveFor(next));
      }
    };

    /* ── Schematic stand-in, used only if the GLB can't be fetched ────── */
    const buildSchematicCar = (damageMap) => {
      const partMat = (partKey) => {
        const severity = damageMap?.[partKey];
        const material = new THREE.MeshStandardMaterial({
          color: severity ? SEV_COLORS[severity] : PAINT,
          roughness: severity ? 0.5 : 0.42,
          metalness: 0.12,
        });
        ownedMaterials.push(material);
        return material;
      };
      const box = (
        partKey,
        w,
        h,
        d,
        x,
        y,
        z,
        rz = 0,
        material = null,
        pick = true,
      ) => {
        const geometry = new THREE.BoxGeometry(w, h, d);
        ownedGeometries.push(geometry);
        const mesh = new THREE.Mesh(geometry, material || partMat(partKey));
        mesh.position.set(x, y, z);
        mesh.rotation.z = rz;
        mesh.name = partKey;
        mesh.castShadow = true;
        car.add(mesh);
        if (pick) recordMesh(mesh, partKey);
        return mesh;
      };
      const glassM = new THREE.MeshStandardMaterial({
        color: GLASS,
        roughness: 0.15,
        metalness: 0.4,
        transparent: true,
        opacity: 0.8,
      });
      ownedMaterials.push(glassM);

      box("shell", 4.1, 0.58, 1.8, 0, 0.66, 0);
      box("front_bumper", 0.42, 0.52, 1.88, 2.06, 0.62, 0);
      box("rear_bumper", 0.42, 0.52, 1.88, -2.06, 0.62, 0);
      box("bonnet", 1.18, 0.1, 1.68, 1.3, 1.0, 0, -0.09);
      box("boot", 0.62, 0.1, 1.68, -1.74, 1.02, 0, 0.05);
      box("roof", 1.28, 0.08, 1.5, -0.35, 1.46, 0);
      box("windshield", 0.72, 0.05, 1.46, 0.52, 1.23, 0, -0.72, glassM, false);
      box("rear_glass", 0.6, 0.05, 1.46, -1.22, 1.26, 0, 0.65, glassM, false);
      box("side_glass", 1.2, 0.34, 1.42, -0.35, 1.2, 0, 0, glassM, false);
      box("fender_r", 0.85, 0.52, 0.07, 1.42, 0.72, -0.925);
      box("fender_l", 0.85, 0.52, 0.07, 1.42, 0.72, 0.925);
      box("door_fr", 0.95, 0.58, 0.07, 0.38, 0.76, -0.925);
      box("door_fl", 0.95, 0.58, 0.07, 0.38, 0.76, 0.925);
      box("door_rr", 0.9, 0.58, 0.07, -0.6, 0.76, -0.925);
      box("door_rl", 0.9, 0.58, 0.07, -0.6, 0.76, 0.925);
      box("quarter_r", 0.75, 0.52, 0.07, -1.6, 0.72, -0.925);
      box("quarter_l", 0.75, 0.52, 0.07, -1.6, 0.72, 0.925);
      box("lamp_r", 0.14, 0.15, 0.44, 2.2, 0.88, -0.58);
      box("lamp_l", 0.14, 0.15, 0.44, 2.2, 0.88, 0.58);
      box("tail_r", 0.1, 0.14, 0.4, -2.24, 0.88, -0.6);
      box("tail_l", 0.1, 0.14, 0.4, -2.24, 0.88, 0.6);
      box("mirror_r", 0.12, 0.1, 0.22, 0.72, 1.14, -0.99);
      box("mirror_l", 0.12, 0.1, 0.22, 0.72, 1.14, 0.99);

      const wheelG = new THREE.CylinderGeometry(0.36, 0.36, 0.26, 28);
      const hubG = new THREE.CylinderGeometry(0.15, 0.15, 0.28, 20);
      ownedGeometries.push(wheelG, hubG);
      const tireM = new THREE.MeshStandardMaterial({
        color: TRIM,
        roughness: 0.6,
        metalness: 0.2,
      });
      const hubM = new THREE.MeshStandardMaterial({
        color: 0x8b94a1,
        roughness: 0.3,
        metalness: 0.6,
      });
      ownedMaterials.push(tireM, hubM);
      [
        [1.42, -0.95, "wheel_fr"],
        [1.42, 0.95, "wheel_fl"],
        [-1.42, -0.95, "wheel_rr"],
        [-1.42, 0.95, "wheel_rl"],
      ].forEach(([x, z, partKey]) => {
        const wheel = new THREE.Mesh(wheelG, tireM.clone());
        wheel.rotation.x = Math.PI / 2;
        wheel.position.set(x, 0.36, z);
        wheel.castShadow = true;
        car.add(wheel);
        recordMesh(wheel, partKey);
        const hub = new THREE.Mesh(hubG, hubM);
        hub.rotation.x = Math.PI / 2;
        hub.position.set(x, 0.36, z);
        car.add(hub);
      });
    };

    /* ── Controls ────────────────────────────────────────────────────── */
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0.7, 0);
    controls.enableDamping = true;
    controls.enablePan = false;

    // Wheel zoom is OFF by default so the page scrolls normally. The canvas is
    // ~470px tall and sits mid-page, so the cursor is usually over it while
    // reading — with zoom on the wheel, scrolling the report zoomed the car
    // instead, and the page appeared stuck.
    //
    // OrbitControls' wheel handler returns before preventDefault() when
    // enableZoom is false, so the event falls through to the page untouched.
    // Zoom stays available two ways: hold the platform modifier, or use the
    // on-canvas buttons (no modifier, and discoverable).
    controls.enableZoom = false;

    // Touch: one finger scrolls the PAGE, two rotate the model.
    //
    // OrbitControls sets domElement.style.touchAction = "none" when it
    // connects, which on a phone means the page stops scrolling the moment a
    // finger lands on the canvas — and the canvas is a tall panel in the
    // middle of the report, so that is most of the screen. Reserving rotation
    // for a second finger is the same trade the wheel already makes above:
    // reading the page wins by default, manipulating it is deliberate.
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(max-width: 1023px)").matches
    ) {
      controls.touches = { ONE: null, TWO: THREE.TOUCH.DOLLY_ROTATE };
      renderer.domElement.style.touchAction = "pan-y";
    }

    controls.minDistance = 3.5;
    controls.maxDistance = 10;
    controls.maxPolarAngle = 1.42;
    controls.autoRotate = autoRotate;
    controls.autoRotateSpeed = 0.9;
    renderer.domElement.addEventListener(
      "pointerdown",
      () => {
        controls.autoRotate = false;
      },
      { once: true },
    );

    /* ── Picking ─────────────────────────────────────────────────────── */
    const ray = new THREE.Raycaster();
    const ptr = new THREE.Vector2();

    /* ── Wheel-zoom gating ───────────────────────────────────────────── */
    // Cmd on Apple platforms, Ctrl elsewhere — matching what each platform
    // already uses for zoom.
    const isApple = /Mac|iPhone|iPad|iPod/.test(navigator.platform || "");
    const zoomKeyLabel = isApple ? "⌘" : "Ctrl";
    const heldZoomKey = (event) => (isApple ? event.metaKey : event.ctrlKey);

    const setZoomEnabled = (on) => {
      controls.enableZoom = on;
    };

    const onKeyDown = (event) => {
      if (isApple ? event.key === "Meta" : event.key === "Control") {
        setZoomEnabled(true);
      }
    };
    const onKeyUp = (event) => {
      if (isApple ? event.key === "Meta" : event.key === "Control") {
        setZoomEnabled(false);
      }
    };
    // A modifier released while the window is unfocused never fires keyup, which
    // would leave the wheel hijacked.
    const onWindowBlur = () => setZoomEnabled(false);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWindowBlur);

    // Tell the user why their scroll did not zoom, rather than leaving them to
    // guess. Only while actually wheeling over the canvas, and self-dismissing.
    let hintTimer = null;
    const zoomHint = document.createElement("div");
    zoomHint.style.cssText =
      "position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);pointer-events:none;" +
      "background:rgba(19,23,32,0.82);color:#fff;font:600 12px 'Schibsted Grotesk',sans-serif;" +
      "padding:7px 12px;border-radius:8px;opacity:0;transition:opacity .18s;z-index:4;white-space:nowrap";
    zoomHint.textContent = `Hold ${zoomKeyLabel} and scroll to zoom`;
    host.appendChild(zoomHint);

    const onWheel = (event) => {
      // Modifier held: OrbitControls handles it and preventDefaults, so neither
      // the page nor the browser zooms.
      if (heldZoomKey(event)) {
        zoomHint.style.opacity = 0;
        return;
      }
      zoomHint.style.opacity = 1;
      if (hintTimer) clearTimeout(hintTimer);
      hintTimer = setTimeout(() => {
        zoomHint.style.opacity = 0;
      }, 1100);
    };
    // Passive: this listener never blocks the scroll it is describing.
    renderer.domElement.addEventListener("wheel", onWheel, { passive: true });

    const pick = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ptr.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );
      ray.setFromCamera(ptr, camera);
      const hit = ray.intersectObjects(picks, false)[0];
      return hit ? partOfMesh.get(hit.object) || null : null;
    };

    const onMove = (event) => {
      const partKey = pick(event);
      if (partKey) cancelHint();
      if (partKey !== hovered) {
        if (hovered && hovered !== selected) setPartEmissive(hovered, 0);
        hovered = partKey;
        if (partKey && partKey !== selected)
          setPartEmissive(partKey, HOVER_EMISSIVE);
      }
      renderer.domElement.style.cursor = partKey ? "pointer" : "grab";
      if (partKey) {
        const severity = damageRef.current?.[partKey];
        tip.textContent =
          (LABELS[partKey] || partKey) +
          (severity ? ` · ${severity}` : " · no damage");
        tip.style.opacity = 1;
        const hostRect = host.getBoundingClientRect();
        tip.style.left = `${event.clientX - hostRect.left + 12}px`;
        tip.style.top = `${event.clientY - hostRect.top - 10}px`;
      } else {
        tip.style.opacity = 0;
      }
    };
    const onLeave = () => {
      if (hovered && hovered !== selected) setPartEmissive(hovered, 0);
      hovered = null;
      tip.style.opacity = 0;
    };
    const onDown = (event) => {
      downAt = [event.clientX, event.clientY, Date.now()];
    };
    const onUp = (event) => {
      if (!downAt) return;
      const moved = Math.hypot(
        event.clientX - downAt[0],
        event.clientY - downAt[1],
      );
      if (moved < 6 && Date.now() - downAt[2] < 500) {
        const partKey = pick(event);
        if (partKey) {
          cancelHint();
          // No optimistic highlight here: the parent may legitimately reject the
          // click — the mesh has pickable panels the assessment never reports —
          // and since selectedPart would not change, nothing would come back to
          // correct a flash we had already shown. The prop round-trip drives the
          // animation instead, via the selectedPart effect, one frame later.
          if (cbRef.current) cbRef.current(partKey);
        }
      }
      downAt = null;
    };
    renderer.domElement.addEventListener("pointermove", onMove);
    renderer.domElement.addEventListener("pointerleave", onLeave);
    renderer.domElement.addEventListener("pointerdown", onDown);
    renderer.domElement.addEventListener("pointerup", onUp);

    /* ── Sizing / render loop ────────────────────────────────────────── */
    // Size is followed from the render loop rather than through an observer.
    // Resizing the renderer inside a ResizeObserver callback re-entered the
    // observer, and Chrome threw "ResizeObserver loop completed with
    // undelivered notifications" — which CRA's dev overlay surfaces as an
    // uncaught error. The loop already runs every frame, and this only touches
    // the renderer when the dimensions actually change.
    let lastW = 0;
    let lastH = 0;
    const fit = () => {
      const w = host.clientWidth;
      const h = host.clientHeight;
      if (!w || !h || (w === lastW && h === lastH)) return;
      lastW = w;
      lastH = h;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    fit();

    let running = true;
    const io = new IntersectionObserver(([entry]) => {
      running = entry.isIntersecting;
    });
    io.observe(host);

    // easeOutCubic — fast off the peak, so the flash reads as a reaction to the
    // click rather than a slow glow.
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const advanceHighlights = (now) => {
      if (flash) {
        const t = (now - flash.start) / SELECT_FLASH_MS;
        const steadyLevel = steadyEmissiveFor(flash.partKey);
        if (t >= 1) {
          // Only settle the part still selected; a rapid second click may have
          // moved on already.
          if (flash.partKey === selected) {
            setPartEmissive(flash.partKey, steadyLevel);
          }
          flash = null;
        } else {
          const level =
            steadyLevel + (SELECT_FLASH_PEAK - steadyLevel) * (1 - easeOut(t));
          setPartEmissive(flash.partKey, level);
        }
      }

      if (hint) {
        const t = (now - hint.start) / HINT_PULSE_MS;
        // start is deliberately in the future (a beat after the model appears),
        // so t is negative until then — sin() of a negative would drive the
        // emissive below zero.
        if (t < 0) return;
        if (t >= 1) {
          cancelHint();
        } else {
          // One full sine arch: up and back down, no hard edges.
          const level = Math.sin(Math.PI * t) * HINT_PULSE_PEAK;
          hint.parts.forEach((key) => {
            if (key === selected || key === hovered) return;
            setPartEmissive(key, level);
          });
        }
      }
    };

    renderer.setAnimationLoop(() => {
      if (!running) return;
      fit();
      advanceHighlights(performance.now());
      if (goal) {
        camera.position.lerp(goal, 0.08);
        if (camera.position.distanceTo(goal) < 0.02) goal = null;
      }
      controls.update();
      renderer.render(scene, camera);
    });

    apiRef.current = {
      // Buttons zoom without any modifier: move along the current view axis and
      // let the render loop's existing lerp animate it.
      zoom: (factor) => {
        const offset = camera.position.clone().sub(controls.target);
        const distance = THREE.MathUtils.clamp(
          offset.length() * factor,
          controls.minDistance,
          controls.maxDistance,
        );
        goal = controls.target
          .clone()
          .add(offset.normalize().multiplyScalar(distance));
        controls.autoRotate = false;
      },
      select: (partKey) => applySelect(partKey, { animate: true }),
      damage: applyDamage,
      view: (name) => {
        const target = VIEWS[name];
        if (!target) return;
        goal = new THREE.Vector3(...target);
        controls.autoRotate = false;
      },
    };

    /* ── Model ───────────────────────────────────────────────────────── */
    const finish = () => {
      applyDamage(damageRef.current);
      // The initial selection is not a user action, so it settles without a
      // flash — a flash on load would train the user to ignore it.
      applySelect(selectedRef.current);

      // Advertise that the panels are interactive, once, and only if the user
      // has not already started poking at the model.
      if (!hintDone && !hovered) {
        const damaged = Object.keys(damageRef.current || {}).filter((key) =>
          parts.has(key),
        );
        if (damaged.length) {
          hint = { start: performance.now() + 250, parts: damaged };
        }
      }
      if (viewRef.current && viewRef.current !== "threeq") {
        apiRef.current?.view(viewRef.current);
      }
    };

    const onProgress = (event) => {
      if (disposed || !event.total) return;
      setProgress(Math.round((event.loaded / event.total) * 100));
    };

    loadVehicleModel(publicUrl(profile.file), onProgress)
      .then((template) => {
        if (disposed) return;
        const instance = template.clone(true);

        // Drop decorative glows first: registration snapshots each material's
        // emissive as the value to restore after a hover, so this has to happen
        // before that or the glow would come back on mouse-out. Cloning keeps
        // the cached template — shared with later mounts — untouched.
        instance.traverse((child) => {
          if (!child.isMesh) return;
          const list = Array.isArray(child.material)
            ? child.material
            : [child.material];
          if (!list.some(isDecorativeGlow)) return;
          const strip = (material) => {
            if (!isDecorativeGlow(material)) return material;
            const copy = material.clone();
            copy.emissive.setScalar(0);
            copy.emissiveIntensity = 1;
            ownedMaterials.push(copy);
            return copy;
          };
          child.material = Array.isArray(child.material)
            ? child.material.map(strip)
            : strip(child.material);
        });

        // Models disagree on where `extras.partId` lives: the sedan tags the
        // group that wraps an assembly (its front bumper is 10 meshes), while
        // the coupe tags each mesh directly. Register the whole subtree of any
        // tagged node so both conventions work. Deepest tags are applied first
        // and `recordMesh` ignores an already-claimed mesh, so a nested part
        // beats its ancestor rather than being swallowed by it.
        const tagged = [];
        const collect = (node, depth) => {
          const partKey = profile.partIds[node.userData?.partId];
          if (partKey) tagged.push({ node, partKey, depth });
          node.children.forEach((child) => collect(child, depth + 1));
        };
        collect(instance, 0);
        tagged
          .sort((a, b) => b.depth - a.depth)
          .forEach(({ node, partKey }) =>
            node.traverse((child) => {
              if (child.isMesh) recordMesh(child, partKey);
            }),
          );
        car.add(instance);
        orientModel(car);
        finish();
        setStatus("ready");
      })
      .catch((error) => {
        if (disposed) return;
        console.error("Could not load vehicle model, using schematic:", error);
        buildSchematicCar(damageRef.current);
        finish();
        setStatus("fallback");
      });

    return () => {
      disposed = true;
      apiRef.current = null;
      io.disconnect();
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWindowBlur);
      renderer.domElement.removeEventListener("wheel", onWheel);
      if (hintTimer) clearTimeout(hintTimer);
      zoomHint.remove();
      renderer.domElement.removeEventListener("pointermove", onMove);
      renderer.domElement.removeEventListener("pointerleave", onLeave);
      renderer.domElement.removeEventListener("pointerdown", onDown);
      renderer.domElement.removeEventListener("pointerup", onUp);
      renderer.setAnimationLoop(null);
      controls.dispose();
      envRT.dispose();
      // Only dispose what this mount allocated — the cached model's geometries
      // are shared with future mounts.
      ownedMaterials.forEach((material) => material.dispose());
      ownedGeometries.forEach((geometry) => geometry.dispose());
      ground.geometry.dispose();
      ground.material.dispose();
      renderer.dispose();

      // Remove exactly the three nodes this effect appended, and nothing else.
      // `host` is a React-rendered div with React-rendered children (the zoom
      // buttons, the loading overlay), so clearing it wholesale — innerHTML =
      // "" — deletes nodes React still believes it owns. The next render that
      // unmounts one of them then throws NotFoundError from removeChild. That
      // is guaranteed on a model switch: cleanup wipes the buttons, the effect
      // immediately sets status back to "loading", and React tries to remove a
      // node that is already gone.
      renderer.domElement.remove();
      tip.remove();
    };
  }, [model, autoRotate]);

  useEffect(() => {
    apiRef.current?.damage(damage);
    // Re-tinting is keyed on the map's contents, not its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(damage)]);

  useEffect(() => {
    apiRef.current?.select(selectedPart);
  }, [selectedPart]);

  useEffect(() => {
    apiRef.current?.view(viewPreset);
  }, [viewPreset]);

  return (
    <div
      ref={hostRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 300,
      }}
    >
      {/* Zoom without a modifier, and a visible cue that zoom exists at all —
          with the wheel handed back to the page, buttons are the discoverable
          path. Kept off the bottom-left, where the page's own interaction hint
          sits. */}
      {status !== "loading" && (
        <div
          style={{
            position: "absolute",
            right: 10,
            bottom: 10,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            zIndex: 3,
          }}
        >
          {[
            { label: "+", factor: 0.78, title: "Zoom in" },
            { label: "−", factor: 1.28, title: "Zoom out" },
          ].map((button) => (
            <button
              key={button.label}
              type="button"
              title={button.title}
              aria-label={button.title}
              onClick={() => apiRef.current?.zoom(button.factor)}
              style={{
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 7,
                border: "1px solid var(--cl-border-card)",
                background: "rgba(255,255,255,0.92)",
                color: "var(--cl-text)",
                font: "600 15px 'Schibsted Grotesk',sans-serif",
                lineHeight: 1,
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(19,23,32,0.08)",
              }}
            >
              {button.label}
            </button>
          ))}
        </div>
      )}

      {status === "loading" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            font: "500 12.5px 'Schibsted Grotesk',sans-serif",
            color: "#7a8291",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          {progress ? `Loading 3D model… ${progress}%` : "Loading 3D model…"}
        </div>
      )}
    </div>
  );
}
